/**
 * Сервис регулярной доставки
 * @module API/delivery
 * */

import stores from 'PageShop/stores'
import client from 'API/client'

const GEO_PREFIX = 'geo/'
const REGION_PREFIX = 'shops/region/'

/**
 * Получить список регионов
 * @function
 * @param {Stirng} name - строка,
 * по которой будут отфильтрованы регионы (пустая строка вернёт все регионы)
 * @return {Promise}
 * */
export const searchRegion = (name) => client.get(`${GEO_PREFIX}regions`, { params: { name } })

/**
 * Получить список магазинов по id региона
 * @function
 * @param {String} region_id
 * @return {Promise}
 * */
export const getStoresByRegionId = (region_id) => client.get(`${REGION_PREFIX}shop`, {
  params: {
    region_id
  }
})

/**
 * Получить подробную информацию о магазине по его id
 * @function
 * @return {Promise}
 * */
export const getStoreById = (storeId) => ({ data: stores.find(store => store.id === storeId) })

/**
 * Получить подробную информацию о настройках магазина по его id
 * @function
 * @param {String} shop_id - uuid магазина
 * @return {Promise}
 * */
export const getSettingsByStoreId = (shop_id) => client.get('delivery/settings', { params: { shop_id } })

/**
 * Создать настройки для магазина
 * По умолчанию у магазина нет настроек.
 * У магазина может быть несколько настроек.
 * @function
 * @param {String} params.shop_id - uuid магазина
 * @param {Number} params.delivery_boundaries - через сколько часов расформировать заказ
 * @param {Number} params.max_height - максимальная высота для товара
 * @param {Number} params.max_length - максимальная длина для товара
 * @param {Number} params.max_width - максимальная ширина для товара
 * @param {Number} params.max_volume - максимальный объём для заказа
 * @param {Number} params.max_weight - максимальный вес для заказа
 * @return {Promise}
 * */
export const createSetting = (params) => client.post('/delivery/setting', params)

/**
 * Обновить значения настроек магазина
 * @function
 * @param {String} params.shop_id - uuid магазина
 * @param {Number} params.delivery_boundaries - через сколько часов расформировать заказ
 * @param {Number} params.max_height - максимальная высота для товара
 * @param {Number} params.max_length - максимальная длина для товара
 * @param {Number} params.max_width - максимальная ширина для товара
 * @param {Number} params.max_volume - максимальный объём для заказа
 * @param {Number} params.max_weight - максимальный вес для заказа
 * @return {Promise}
 * */
export const updateSettings = (params) => client.patch(`/delivery/setting/${params.id}`, params)

/**
 * Получить информацию о самовывозе магазина по его id
 * @function
 * @return {Promise}
 * */
// eslint-disable-next-line
export const getPickupByStoreId = (shop_id) => client.get(`/pickup/options`, { params: { shop_id } })

/**
 * Создать опцию для магазина
 * У магазина может быть несколько опций
 * Каждая опция включает поле "расформировать заказ через Н часов"
 * и интервалы самовывоза
 * (на текущий момент работаем только с одной опцией)
 * @function
 * @param {String} shop_id - uuid магазина
 * @return {Promise}
 * */
export const createOption = (shop_id) => client.post('/pickup/option', { shop_id })

/**
 * Обновить опцию магазина
 * для обновления используется (delivery_option_id || (area_id && shop_id))
 * @function
 * @param {String} delivery_option_id - uuid опции магазина
 * @param {String} area_id - uuid зоны
 * @param {String} shop_id - uuid магазина
 * @param {Array} intervals - интервалы самовывоза (Interval)
 *
 * @param {String} Interval.available_from - время начала интервала (hh:mm)
 * @param {Stirng} Interval.available_to - время окончания интервала (hh:mm)
 * @param {Boolean} Interval.is_acrive - включен или выключен
 * @param {Number} Interval.blocking_for - за сколько часов в интервал перестают попадать заказы
 * @param {Array} Interval.daily_restrictions - настройки дней (Day)
 *
 * @param {String} Day.code - код дня (monday | tuesday | wednesday | ...)
 * @param {Boolean} Day.is_active - включен ли этот день для доставки
 * @param {Number} Day.max_count_of_orders - максимальное количество заказов в этот день
 *
 * @return {Promise}
 * */
export const updateOption = (params) => client.patch(`/pickup/option/${params.optionId}`, { expire_offset: params.expire_offset })

/**
 * Обновить интервалы самовывоза
 * Интервалы есть у опции, которая есть у магазина
 * @function
 * @param {String} delivery_option_id - uuid опции
 * @param {Array} intervals - интервалы самовывоза (Interval)
 *
 * @param {String} Interval.available_from - время начала интервала (hh:mm)
 * @param {Stirng} Interval.available_to - время окончания интервала (hh:mm)
 * @param {Boolean} Interval.is_acrive - включен или выключен
 * @param {Number} Interval.blocking_for - за сколько часов в интервал перестают попадать заказы
 * @param {Array} Interval.daily_restrictions - настройки дней (Day)
 *
 * @param {String} Day.code - код дня (monday | tuesday | wednesday | ...)
 * @param {Boolean} Day.is_active - включен ли этот день для доставки
 * @param {Number} Day.max_count_of_orders - максимальное количество заказов в этот день
 *
 * @return {Promise}
 * */
export const updatePickupIntervals = (params) => client.post('/pickup_option/interval', params)

/**
 * Получить зоны магазина
 * @function
 * @param {String} storeId - uuid магазина
 * @return {Promise}
 * */
// eslint-disable-next-line
export const getZonesByStoreId = (storeId) => ({ data: stores.find(store => store.id === storeId).zones })

/**
 * Удалить зону магазина
 * @function
 * @return {Promise}
 * */
export const removeZone = () => {

}
